import { Divider, TextButton, Popup } from "@cdkglobal/radial";
import {
  DepartmentRow,
  DepartmentTitle,
  SectionTitle,
  StyledAccountingIcon,
  StyledDepartmentAccordion,
  StyledPartsIcon,
  StyledSalesIcon,
  StyledServiceIcon,
} from "../../../../utils/styled";
import { SetupCardContent } from "./SetupCards";
import {
  IOption,
  NavItem,
  SetupListNavInfoByDepartment,
} from "../../../../types";
import { useUserContext } from "../../../../contexts/UserContext";

const iconMap: Record<string, React.ComponentType> = {
  accounting: StyledAccountingIcon,
  parts: StyledPartsIcon,
  sales: StyledSalesIcon,
  service: StyledServiceIcon,
};

export function DepartmentAccordion({
  filteredDepartmentOptions,
  filteredSetupCards,
  openPanel,
  setOpenPanel,
  handleSetupClick,
  departments,
}: Readonly<{
  filteredDepartmentOptions: string[];
  filteredSetupCards: SetupListNavInfoByDepartment;
  openPanel: Record<string, boolean>;
  setOpenPanel: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  handleSetupClick: ({
    setup,
    department,
  }: {
    setup: IOption;
    department: IOption;
  }) => void;
  departments: IOption[];
}>): JSX.Element {
  const { favorites, setFavorites } = useUserContext();

  const handleOpenPanel = (itemId: string) => {
    setOpenPanel((prev) => ({
      ...prev,
      [itemId]: true,
    }));
  };

  const handleClosePanel = (itemId: string) => {
    setOpenPanel((prev) => ({
      ...prev,
      [itemId]: false,
    }));
  };

  const departmentOptions =
    filteredDepartmentOptions.length > 0
      ? departments.filter((d) => filteredDepartmentOptions.includes(d.value))
      : departments;

  const toggleFavorite = (setup: NavItem) => {
    setFavorites((prev) => {
      const exists = prev.some((item) => item.id === setup.id);
      if (exists) {
        return prev.filter((item) => item.id !== setup.id);
      }
      return [...prev, setup];
    });
  };

  return (
    <div>
      {departmentOptions?.map((department: IOption) => {
        const IconComponent = iconMap[department.value];
        const isOpen = openPanel[department.value] || false;
        const setupsItems = filteredSetupCards[department.value]?.setups ?? [];

        return (
          <DepartmentRow key={department.value}>
            <Popup
              isOpen={isOpen}
              onClose={() => handleClosePanel(department.value)}
              onOpen={() => handleOpenPanel(department.value)}
              closePopupOnOutsideClick={false}
              dataTestId={`${department.value}-accordion`}
              wrappedComponentContent={
                <StyledDepartmentAccordion
                  onClick={() => isOpen
                      ? handleClosePanel(department.value)
                      : handleOpenPanel(department.value)
                  }
                >
                  <DepartmentTitle>
                    {IconComponent && <IconComponent />}
                    <SectionTitle>{department.label}</SectionTitle>
                  </DepartmentTitle>

                  <TextButton
                    dataTestId={`${department.value}-expand-btn`}
                    text={isOpen ? "View less" : "View More"}
                  />
                </StyledDepartmentAccordion>
              }
            >
              <SetupCardContent
                setupItems={setupsItems}
                handleSetupClick={handleSetupClick}
                favorites={favorites}
                toggleFavorite={toggleFavorite}
              />
            </Popup>
            <Divider data-testid="divider" showMargins />
          </DepartmentRow>
        );
      })}
    </div>
  );
}
